import axiosInstance from "@/lib/axiosInstance";
import type { CustomAxiosRequestConfig } from "@/types/common.types";
import { userManagementEndpoints } from "./userManagement.endpoints";
import type {
  CreateSuperAdminPayload,
  CreateUserPayload,
  CreateUserResponse,
  StartImpersonationPayload,
  SetUserPasswordPayload,
  SetUserPasswordResponse,
  StartImpersonationResponse,
  UpdateSuperAdminPayload,
  UpdateUserPayload,
  UpdateUserResponse,
} from "./userManagement.mutations.types";

export const createUser = async (
  payload: CreateUserPayload,
  config?: CustomAxiosRequestConfig,
): Promise<CreateUserResponse> => {
  const { data } = await axiosInstance.post(userManagementEndpoints.users, payload, config);
  return data;
};

export const createSuperAdmin = async (
  payload: CreateSuperAdminPayload,
  config?: CustomAxiosRequestConfig,
): Promise<CreateUserResponse> => {
  const { data } = await axiosInstance.post(
    userManagementEndpoints.superAdminUsers,
    payload,
    config,
  );
  return data;
};

export const updateSuperAdmin = async (
  userId: string,
  payload: UpdateSuperAdminPayload,
): Promise<CreateUserResponse> => {
  const { data } = await axiosInstance.put(`${userManagementEndpoints.users}/${userId}`, payload);
  return data;
};

/** PUT /user-management/users/:userId - update an existing user. */
export const updateUser = async (
  userId: string,
  payload: UpdateUserPayload,
): Promise<UpdateUserResponse> => {
  const { data } = await axiosInstance.put(`${userManagementEndpoints.users}/${userId}`, payload);
  return data;
};

/** PUT /user-management/users/:userId/password - admin sets a user's password (SERV-3300). */
export const setUserPassword = async (
  userId: string,
  payload: SetUserPasswordPayload,
  /** A company of the person's, when the request would otherwise not be in one of theirs (UI32). */
  actingEntityId?: string,
): Promise<SetUserPasswordResponse> => {
  const { data } = await axiosInstance.put(
    userManagementEndpoints.setUserPassword(userId),
    payload,
    (actingEntityId ? { actingEntityId } : {}) as CustomAxiosRequestConfig,
  );
  return data;
};

/** SERV-3547: ends a pending invite in one entity. Quiet, so the modal says what went wrong. */
export const cancelInvite = async (userId: string, entityId: string): Promise<void> => {
  await axiosInstance.put(userManagementEndpoints.cancelInvite(userId), { entityId }, {
    suppressErrorAlert: true,
  } as CustomAxiosRequestConfig);
};

export const startImpersonation = async (
  payload: StartImpersonationPayload,
): Promise<StartImpersonationResponse> => {
  const { data } = await axiosInstance.post<StartImpersonationResponse>(
    userManagementEndpoints.startImpersonation,
    { targetUserId: payload.userId },
  );
  return data;
};

export const endImpersonation = async (): Promise<void> => {
  await axiosInstance.post(userManagementEndpoints.endImpersonation);
};
